import { useState, useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Sidebar } from '../Sidebar';
import { MobileTabBar } from './MobileTabBar';
import { useScrollLock } from '../../../lib/scrollLock';

const TAB_ROOT_PATTERNS = [
  /^\/[^/]+$/,           // /:storeId
  /^\/[^/]+\/$/,         // /:storeId/
  /^\/[^/]+\/orders/,
  /^\/[^/]+\/products/,
  /^\/[^/]+\/customers/,
];

// Capacitor-only shell. Renders the page content with safe-area padding for
// the notch + home indicator, then a tab bar pinned to the bottom. The fifth
// "More" tab reuses the existing Sidebar as a slide-over panel so all secondary
// routes (Settings, Coupons, Reviews, etc.) remain reachable without
// duplicating that whole menu.
export function MobileLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();

  // Close the More panel automatically when the user navigates from inside it.
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  // Freeze the page <main> behind the sidebar, same as we do for modals.
  // Otherwise pans on the dim backdrop scroll-chain to the page.
  useScrollLock(sidebarOpen);

  const onTabRoot = TAB_ROOT_PATTERNS.some((re) => re.test(location.pathname));

  return (
    <div
      className="flex flex-col h-screen bg-bg-primary overflow-hidden"
      style={{ paddingTop: 'env(safe-area-inset-top)' }}
    >
      <main
        // overflow-x-hidden is explicit because `overflow-y: auto` alone
        // implicitly promotes the other axis to `auto` (CSS spec), letting
        // any wide widget scroll the page sideways into empty space.
        className="flex-1 overflow-y-auto overflow-x-hidden overscroll-contain px-4"
        // Tab bar = 56px <ul> + 1px border-top + env(safe-area-inset-bottom)
        // padding. We add an extra 8px so the last row of content doesn't
        // butt right against the bar's top edge — that "clipped by a hair"
        // feel comes from leaving zero breathing room.
        style={{ paddingBottom: 'calc(56px + 1px + 8px + env(safe-area-inset-bottom))' }}
      >
        <Outlet />
      </main>

      <MobileTabBar
        onMoreTap={() => setSidebarOpen(true)}
        moreActive={!onTabRoot || sidebarOpen}
      />

      {/* Reuse the existing sidebar in slide-over mode for the "More" tab. */}
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
    </div>
  );
}
